/**
 * EL ESTADO DEL DÍA, EN UN SOLO SITIO.
 *
 * Del artefacto del 10-09, «La pestaña de Inicio», que lo pide con todas las letras:
 *
 *     «El mismo estado en los cuatro sitios. Nutrición, la línea de Inicio, Mi semana y la
 *      campana. Se calcula una vez y se escribe igual en todas partes. Hoy Inicio se
 *      inventa el suyo.»
 *
 * Y se lo inventaba de verdad: la línea de mañana leía el objetivo del día en
 * `macros_snapshot.P`, y ahí las claves son `P_total`, `H_total` y `G_total`. Salía cero,
 * así que ningún macro pasaba nunca del margen y la línea NUNCA llegaba a decir cuánto
 * falta. Se quedaba en «dieta creada, sin terminar» tanto si faltaban seis gramos como
 * sesenta, que es exactamente la queja del documento.
 *
 * LA REGLA, que es la del día y no la de la comida: «la comida se da por válida con
 * margen; el día no: el día tiene que cerrar». Tres estados y ninguno más.
 */
import { MARGEN } from './exceso';
import { num0, alDecima } from './numeros';

/** El margen del día: 4 g planos, los mismos que ya aplica todo lo demás. */
export const MARGEN_DEL_DIA = MARGEN;

const MACROS = ['P', 'H', 'G'];
const NOMBRE_LARGO = { P: 'proteína', H: 'hidratos', G: 'grasa' };

const num1 = (v) => (Math.round(v * 10) / 10).toString().replace('.', ',');

/**
 * El objetivo del día desde el `macros_snapshot` que guarda Nutrición.
 *
 * Acepta las dos escrituras porque las dos andan por la base: `P_total` es la que escribe
 * el guardado de hoy, y `P` a secas aparece en apuntes viejos. Con una sola de las dos, la
 * mitad de los días medían contra cero.
 */
export function objetivoDelSnapshot(snapshot) {
    const s = snapshot || {};
    const uno = (k) => Number(s[`${k}_total`] ?? s[k] ?? 0) || 0;
    return { P: uno('P'), H: uno('H'), G: uno('G') };
}

/**
 * Lo servido en un día, sumando TODAS las comidas, el intra y el post incluidos.
 *
 * El peri cuenta: es comida del día y sus macros salen del mismo objetivo. Dejarlo fuera
 * era otra de las cuentas que no cuadraban entre pantallas -- la cabecera de Inicio decía
 * 297,8 de proteína y la línea de mañana sumaba 197,6 del mismo día.
 */
export function servidoDelDia(comidas) {
    const total = { P: 0, H: 0, G: 0 };
    Object.values(comidas || {}).forEach((c) => {
        (c?.alimentos || []).forEach((a) => {
            // Y SI NO HAY `macros_efectivos`, LOS REALES (13-09). Ese campo lo escribe la
            // calculadora al añadir un alimento (`backend/calculator.py`), así que los
            // alimentos que entran por otra puerta -- repetir un día, aplicar una favorita,
            // una plantilla -- llegan sin él. Mirando solo ahí, un día entero montado así
            // sumaba CERO y la línea de mañana no podía decir nunca «dieta lista».
            // Los reales no son lo mismo -- el efectivo descuenta lo que no cuenta, como la
            // proteína de algunos alimentos -- pero medir con ellos se acerca, y medir con
            // cero no es medir.
            const m = a?.macros_efectivos || a?.macros_reales || a?.macros || {};
            MACROS.forEach((x) => { total[x] += Number(m[x]) || 0; });
        });
    });
    return total;
}

/**
 * LOS NÚMEROS DE UN DÍA, PREFIRIENDO LOS DEL SERVIDOR (13-09).
 *
 * `GET /diets/{fecha}` ya devuelve `objetivo_comidas` y `servido_comidas` resueltos, con el
 * perientreno fuera de los dos, y es lo que pinta la cabecera de Nutrición y el Inicio de
 * HOY. La línea de mañana no los usaba: se los calculaba por su cuenta desde el
 * `macros_snapshot` del día y los `macros_efectivos` de cada alimento, y los días montados
 * sin pasar por la calculadora no traen ni una cosa ni la otra. Resultado: objetivo cero,
 * servido cero, y la línea diciendo «dieta creada, sin terminar» de una dieta terminada.
 *
 * Aquí se piden al servidor primero y solo se calcula si no vienen, que es el mismo orden
 * que ya sigue el día de hoy («o ese, o ninguno: no hay segundo camino»).
 */
export function numerosDelDia(dia) {
    const d = dia || {};
    const comidas = (d.exists && d.comidas) || d.comidas || {};
    const delServidor = (x) => (x && MACROS.some((m) => Number(x[m]) > 0)
        ? { P: Number(x.P) || 0, H: Number(x.H) || 0, G: Number(x.G) || 0 } : null);
    return {
        objetivo: delServidor(d.objetivo_comidas) || objetivoDelSnapshot(d.macros_snapshot),
        servido: delServidor(d.servido_comidas) || servidoDelDia(comidas),
    };
}

/**
 * EL TOTAL DEL DÍA, EL MISMO EN INICIO Y EN NUTRICIÓN (Francisco, 12-10: "empareja lo de
 * llevas"; fallo F10 del cuaderno: Inicio 210 · 141 · 60 y Nutrición 209,8 · 141,2 · 59,9).
 *
 * Las dos pantallas enseñan lo creado en el día y lo sacaban cada una por su lado: Inicio
 * leía el total hecho del servidor y Nutrición lo sumaba dentro de la cabecera. Aquí está la
 * cuenta una vez, y las dos la piden con lo que suma cada comida (`{C1: {P, H, G}, Intra:
 * ...}`): Inicio con `servido_por_comida` de `GET /diets/{fecha}`, Nutrición con lo que
 * tiene en pantalla. Cada alimento llega ya a la décima, que es como lo deja la calibración
 * del servidor en los dos caminos.
 *
 * La regla es la del método y la misma de `_total_como_arriba` (backend/routes/diets.py):
 * la proteína y los hidratos llevan el perientreno dentro; la grasa no, porque el peri no
 * gasta grasa del día.
 */
const PERI = ['Intra', 'Post'];
export function totalDelDia(porComida) {
    const total = { P: 0, H: 0, G: 0 };
    Object.entries(porComida || {}).forEach(([k, m]) => {
        total.P += Number(m?.P) || 0;
        total.H += Number(m?.H) || 0;
        if (!PERI.includes(k)) total.G += Number(m?.G) || 0;
    });
    // A la décima y sin la cola de la coma flotante (209,79999999999998).
    const limpio = (v) => Number(v.toFixed(1));
    return { P: limpio(total.P), H: limpio(total.H), G: limpio(total.G) };
}

/**
 * Y UN SOLO REDONDEO PARA ESCRIBIRLO: el número grande del día va REDONDO en las dos
 * pantallas (Francisco, 16-09: "si dice 192,2 queda en 192 pero si dice 192,6 queda en
 * 193"). Aquel día se hizo solo en Inicio y Nutrición se quedó a la décima: el mismo día
 * decía 210 en una y 209,8 en la otra. La décima sigue dentro de cada comida y de cada
 * alimento, que es donde se afina; el objetivo del día se escribe con esta misma función.
 * Es el `num0` de siempre (lib/numeros) sobre el valor a la décima, tal como lo hacía Inicio.
 */
export const cifraDelDia = (valor) => num0(alDecima(valor));

/** Cuánto se desvía cada macro: positivo si sobra, negativo si falta. */
export function desfaseDelDia(servido, objetivo) {
    return {
        P: (servido?.P || 0) - (objetivo?.P || 0),
        H: (servido?.H || 0) - (objetivo?.H || 0),
        G: (servido?.G || 0) - (objetivo?.G || 0),
    };
}

/**
 * Los tres estados del documento, más el de pasarse.
 *
 *   'sin_crear'    · no hay nada montado
 *   'listo'        · los tres dentro del margen: el día cierra
 *   'se_pasa'      · algún macro por encima del margen (se devuelve `null` en la banda,
 *                    que tiene su propia tarjeta naranja para esto)
 *   'sin_terminar' · le falta
 */
export function estadoDelDia(servido, objetivo, { hayAlgo = true } = {}) {
    if (!hayAlgo) return 'sin_crear';
    // Sin objetivo no se juzga: decir que un día no cuadra sin saber qué se le pedía es
    // acusarle de algo que no se ha medido. Es la misma salvedad del juez del servidor.
    if (!MACROS.some((m) => (objetivo?.[m] || 0) > 0)) return null;
    const d = desfaseDelDia(servido, objetivo);
    // PASARSE DE PROTEÍNA NO ES PASARSE (Jesús, 13-08), y esta es la parte que tenía el
    // front distinta del servidor. `core/dia_cuadrado` lo dice así desde entonces, y de esa
    // regla comen Mi semana, el calendario y el «cuadraste N días» del informe del mes; si
    // aquí se contase, el mismo día saldría verde en Mi semana y naranja en Nutrición, que
    // es justo lo que el documento del 10-09 prohíbe.
    // El 24-09 se quitó y Francisco la devolvió el mismo día: no volver a quitarla sin
    // preguntarle. (En cada comida la proteína sí cuenta, desde el 5-09.)
    const proteinaHecha = d.P >= -MARGEN_DEL_DIA;
    const restoCuadra = ['H', 'G'].every((m) => Math.abs(d[m]) <= MARGEN_DEL_DIA);
    if (proteinaHecha && restoCuadra) return 'listo';
    if (['H', 'G'].some((m) => d[m] > MARGEN_DEL_DIA)) return 'se_pasa';
    return 'sin_terminar';
}

/**
 * Lo que le falta, dicho entero: «6,6 g de proteína y 4,5 de grasa».
 * Solo lo que falta: lo que sobra tiene su propio aviso.
 *
 * CON EL REDONDEO DE QUIEN LLAMA (16-09). Desde Inicio y desde Mi semana entra `num0`,
 * porque ahí los números van redondos y la frase no puede llevar una coma que los números
 * de al lado no tienen; en Nutrición, que es donde se afina, se queda la décima. Lo que NO
 * cambia con el redondeo es a quién se nombra: eso lo decide el margen, sobre el desfase
 * exacto, así que un macro dentro de margen no aparece aquí aunque redondeando se viera.
 */
export function loQueFaltaAlDia(servido, objetivo, n = num1) {
    const d = desfaseDelDia(servido, objetivo);
    // LA UNIDAD, EN TODOS (Francisco, 12-09: «las letras de los macros van a ir siempre, si
    // en el artefacto no las tiene no lo tengas en cuenta»). El artefacto la escribe sólo en
    // el primero -- «6,6 g de proteína y 4,5 de grasa» --; aquí manda él.
    const faltan = MACROS
        .filter((m) => d[m] < -MARGEN_DEL_DIA)
        .map((m) => `${n(Math.abs(d[m]))} g de ${NOMBRE_LARGO[m]}`);
    if (!faltan.length) return null;
    return faltan.length === 1 ? faltan[0]
        : `${faltan.slice(0, -1).join(', ')} y ${faltan[faltan.length - 1]}`;
}

/**
 * Y LO QUE SOBRA, dicho igual: «33 g de hidratos» (Jesús, vídeo del 20-09).
 *
 * Faltaba la mitad de la frase. La cabecera sólo sabía decir lo que falta, así que un día
 * pasado de hidratos no tenía nada que decir de sí mismo y caía en el texto de «día sin
 * crear»: «me pone que el día está listo si no está listo» y «no entiendo qué quiere decir
 * eso, si falta, si sobra o qué». Con esto, el día que se pasa puede decirlo con su número.
 */
export function loQueSobraAlDia(servido, objetivo, n = num1) {
    const d = desfaseDelDia(servido, objetivo);
    const sobran = MACROS
        .filter((m) => d[m] > MARGEN_DEL_DIA)
        .map((m) => `${n(d[m])} g de ${NOMBRE_LARGO[m]}`);
    if (!sobran.length) return null;
    return sobran.length === 1 ? sobran[0]
        : `${sobran.slice(0, -1).join(', ')} y ${sobran[sobran.length - 1]}`;
}

/**
 * Lo mismo, corto, para la línea de una sola fila: «6,6 P y 4,5 G».
 * Es la forma que dibuja el documento en la línea de mañana.
 */
export function faltaCorta(servido, objetivo, n = num1) {
    const d = desfaseDelDia(servido, objetivo);
    const faltan = MACROS
        .filter((m) => d[m] < -MARGEN_DEL_DIA)
        .map((m) => `${n(Math.abs(d[m]))} ${m}`);
    if (!faltan.length) return null;
    return faltan.length === 1 ? faltan[0]
        : `${faltan.slice(0, -1).join(', ')} y ${faltan[faltan.length - 1]}`;
}
